import parse from "html-react-parser";
import * as _ from "lodash";
import * as React from "react";
import { useEffect, useState } from "react";
import { encodeXML } from "../../classes/XmlHelper";
import { IConfig } from "../../Config";
import { ViewType } from "../../NewsroomView";
import ArchiveButton from "../RenderComponents/ArchiveButton";
import DeleteButton from "../RenderComponents/DeleteButton";
import EditButton from "../RenderComponents/EditButton";
import FilterPopup from "../RenderComponents/FilterPopup";
import RssFeedButton from "../RenderComponents/RssFeedButton";
import SearchTextbox from "../RenderComponents/SearchTextbox";
import AddPostButton from "./components/AddPostButton";
import Filter from "./components/Filter";
import ReadMoreButton from "./components/ReadMoreButton";
import SettingsButton from "./components/SettingsButton";
import ShowArchivedButton from "./components/ShowArchivedButton";
import "./posts.css";

interface IProps {
    allCategories: any[];
    allTags: string[];
    isAdmin: boolean;
    posts: any[];
    page: number;
    config: IConfig;
    template: string;
    stylesheet: string;
    activeCategories: any[];
    activeTags: any[];
    isViewingArchivedItems: boolean;
    pagination: JSX.Element;
    showAuthor: boolean;
    searchText: string;
    updateSelectedCategoriesAndTags: (selectedCategories: string[], selectedTags: string[]) => void;
    onSettings: () => void;
    onShowArchived: () => void;
    onDelete: (newsId, isDeleted, isPermanent) => void;
    renderFiltersForCategories: () => JSX.Element;
    renderFiltersForTags: () => JSX.Element;
    onSearch: (text: string) => void;
    onSwitchView: (view: ViewType, newsId?: string) => void;
    writableFeedIds: string[];
}

const Template = (props: IProps) => {
    const [html, setHtml] = useState("");
    const [visiblePosts, setVisiblePosts] = useState([]);

    useEffect(() => {
        const visiblePosts = [];
        _.forEach(props.posts, (post) => {
            if ((_.isEmpty(props.activeCategories) && _.isEmpty(props.activeTags))
                || (_.intersection(props.activeCategories, _.map(post.categories, (category) => category.categoryId)).length > 0
                    || _.intersection(props.activeTags, _.map(post.tags, (tag) => tag.tag)).length > 0)) {
                visiblePosts.push(post);
            }
        });
        setVisiblePosts(visiblePosts || []);
    }, [props.posts, props.activeCategories, props.activeTags]);

    useEffect(() => {
        try {
            if (!visiblePosts || _.isEmpty(props.template)) {
                return;
            }
            const xmlNodes = _.map(visiblePosts, (p) => {
                const pinToTop = p.feeds.some((f) => f.pinToTop === true);
                const publishDate = new Date(Date.parse(p.scheduledDate.replace(/['"]+/g, "")));
                publishDate.setDate(publishDate.getDate() + 1);
                publishDate.setHours(0, 0, 0, 0);
                const readMode = props.isViewingArchivedItems ? "archived" : "news";
                const pathname = `#/${props.config.portletInstanceId}/read/${readMode}/f/${p.friendlyUrlName}`;
                return `
                    <child>
                        <title><![CDATA[${p.title || ""}]]></title>
                        <summary><![CDATA[${p.summary || ""}]]></summary>
                        <pin>${pinToTop}</pin>
                        <news_id>${p.newsId}</news_id>
                        <news_friendlyurl>${p.friendlyUrlName}</news_friendlyurl>
                        <featured_image>${encodeXML(p.featuredImage || "")}</featured_image>
                        <image_title>${encodeXML(p.featuredImageCaption)}</image_title>
                        <published_date>${encodeXML(p.scheduledDate)}</published_date>
                        <show_author>${props.showAuthor}</show_author>
                        <author>${encodeXML(p.author)}</author>
                        <link_of_current_page>${encodeXML(pathname)}</link_of_current_page>
                        <show_rss>true</show_rss>
                        <rss_url>${encodeXML(props.config.rssUrl || "")}</rss_url>
                        <writable>${p.feeds.some((f) => props.writableFeedIds.includes(f.feedId))}</writable>
                    </child>
                `;
            });
            const xmlString = `<?xml version="1.0"?><posts>${xmlNodes.join("")}</posts>`;
            const parser = new DOMParser();
            const xml = parser.parseFromString(xmlString, "text/xml");
            const xsl = parser.parseFromString(props.template, "text/xml");
            const xsltProcessor = new XSLTProcessor();
            xsltProcessor.importStylesheet(xsl);
            const resultDocument = xsltProcessor.transformToFragment(xml, document);
            const tempNode = document.createElement("div");
            tempNode.appendChild(resultDocument);
            setHtml(tempNode.outerHTML);
        } catch (ex) {
            console.error(ex);
        }
    }, [visiblePosts, props.template, props.showAuthor]);

    useEffect(() => {
        if (_.isEmpty(props.stylesheet)) {
            return;
        }

        const head = document.head;
        const link = document.createElement("style");
        link.innerHTML = props.stylesheet;
        head.appendChild(link);

        return () => { head.removeChild(link); };
    }, [props.stylesheet]);

    const getAttributeValue = (domNode, attrName, optional = false) => {
        const attr = (((domNode as any)?.attribs || {})[attrName] || "").trim();
        if (!optional && attr === "") {
            const name = ((domNode as any).name || "").toLowerCase();
            const error = `${name} is missing the "${attrName}" attribute`;
            console.error(error);
            throw new Error(error);
        }
        return attr;
    };

    return (
        <>
            {parse(html, {
                replace: (domNode) => {
                    try {
                        const name = ((domNode as any).name || "").toLowerCase();
                        switch (name) {
                            case "addpostbutton": {
                                const className = getAttributeValue(domNode, "class", true);
                                const type = getAttributeValue(domNode, "type", true);
                                const text = getAttributeValue(domNode, "text", true);
                                const title = getAttributeValue(domNode, "title", true);
                                const ariaLabel = getAttributeValue(domNode, "aria-label", true);
                                const showIcon = getAttributeValue(domNode, "show-icon", true) !== "false";
                                return (
                                    <AddPostButton
                                        isAdmin={props.isAdmin}
                                        config={props.config}
                                        className={className}
                                        type={type}
                                        text={text}
                                        title={title}
                                        ariaLabel={ariaLabel}
                                        showIcon={showIcon}
                                        onCreateNew={() => props.onSwitchView(ViewType.EditForm)}
                                    />
                                );
                            }
                            case "archivebutton": {
                                const newsId = getAttributeValue(domNode, "newsid");
                                const className = getAttributeValue(domNode, "class", true);
                                const type = getAttributeValue(domNode, "type", true);
                                const text = getAttributeValue(domNode, "text", true);
                                const archivedTitle = getAttributeValue(domNode, "archived-title", true);
                                const unarchivedTitle = getAttributeValue(domNode, "unarchived-title", true);
                                const archivedAriaLabel = getAttributeValue(domNode, "archived-aria-label", true);
                                const unarchivedAriaLabel = getAttributeValue(domNode, "unarchived-aria-label", true);
                                const showIcon = getAttributeValue(domNode, "show-icon", true) !== "false";
                                return (
                                    <ArchiveButton
                                        isAdmin={props.isAdmin}
                                        post={_.find(props.posts, { newsId })}
                                        onDelete={props.onDelete}
                                        className={className}
                                        type={type}
                                        text={text}
                                        archivedTitle={archivedTitle}
                                        unarchivedTitle={unarchivedTitle}
                                        archivedAriaLabel={archivedAriaLabel}
                                        unarchivedAriaLabel={unarchivedAriaLabel}
                                        showIcon={showIcon}
                                    />
                                );
                            }
                            case "deletebutton": {
                                const newsId = getAttributeValue(domNode, "newsid");
                                const className = getAttributeValue(domNode, "class", true);
                                const type = getAttributeValue(domNode, "type", true);
                                const text = getAttributeValue(domNode, "text", true);
                                const title = getAttributeValue(domNode, "title", true);
                                const ariaLabel = getAttributeValue(domNode, "aria-label", true);
                                const showIcon = getAttributeValue(domNode, "show-icon", true) !== "false";
                                return (
                                    <DeleteButton
                                        isAdmin={props.isAdmin}
                                        post={_.find(props.posts, { newsId })}
                                        onDelete={props.onDelete}
                                        className={className}
                                        type={type}
                                        text={text}
                                        showIcon={showIcon}
                                    />
                                );
                            }
                            case "editbutton": {
                                const newsId = getAttributeValue(domNode, "newsid");
                                const className = getAttributeValue(domNode, "class", true);
                                const type = getAttributeValue(domNode, "type", true);
                                const text = getAttributeValue(domNode, "text", true);
                                const title = getAttributeValue(domNode, "title", true);
                                const ariaLabel = getAttributeValue(domNode, "aria-label", true);
                                const showIcon = getAttributeValue(domNode, "show-icon", true) !== "false";
                                return (
                                    <EditButton
                                        isAdmin={props.isAdmin}
                                        config={props.config}
                                        post={_.find(props.posts, { newsId })}
                                        className={className}
                                        type={type}
                                        text={text}
                                        showIcon={showIcon}
                                        onSwitchView={props.onSwitchView}
                                    />
                                );
                            }
                            case "filter":
                                return (
                                    <Filter
                                        renderFiltersForCategories={props.renderFiltersForCategories}
                                        renderFiltersForTags={props.renderFiltersForTags}
                                    />
                                );
                            case "filterpopup":
                                const type = getAttributeValue(domNode, "type", true);
                                const text = getAttributeValue(domNode, "text", true);
                                const title = getAttributeValue(domNode, "title", true);
                                const ariaLabel = getAttributeValue(domNode, "aria-label", true);
                                const showIcon = getAttributeValue(domNode, "show-icon", true) !== "false";
                                return (
                                    <FilterPopup
                                        allCategories={props.allCategories}
                                        allTags={props.allTags}
                                        ariaLabel={ariaLabel}
                                        selectedCategories={props.activeCategories}
                                        selectedTags={props.activeTags}
                                        text={text}
                                        title={title}
                                        type={type}
                                        showIcon={showIcon}
                                        updateSelection={props.updateSelectedCategoriesAndTags}
                                    />
                                );
                            case "pagination":
                                return props.pagination;
                            case "readmorebutton": {
                                const newsId = getAttributeValue(domNode, "newsid");
                                const className = getAttributeValue(domNode, "class", true);
                                const type = getAttributeValue(domNode, "type", true);
                                const text = getAttributeValue(domNode, "text", true);
                                const title = getAttributeValue(domNode, "title", true);
                                const ariaLabel = getAttributeValue(domNode, "aria-label", true);
                                const friendlyUrl = getAttributeValue(domNode, "news_friendlyurl");
                                return (
                                    <ReadMoreButton
                                        config={props.config}
                                        isViewingArchivedItems={props.isViewingArchivedItems}
                                        post={_.find(props.posts, { newsId })}
                                        page={props.page}
                                        className={className}
                                        type={type}
                                        text={text}
                                        title={title}
                                        ariaLabel={ariaLabel}
                                        onSwitchView={() => props.onSwitchView(ViewType.Details, friendlyUrl)}
                                    />
                                );
                            }
                            case "rssfeedbutton": {
                                const className = getAttributeValue(domNode, "class", true);
                                const type = getAttributeValue(domNode, "type", true);
                                const text = getAttributeValue(domNode, "text", true);
                                const title = getAttributeValue(domNode, "title", true);
                                const ariaLabel = getAttributeValue(domNode, "aria-label", true);
                                const showIcon = getAttributeValue(domNode, "show-icon", true) !== "false";
                                return (
                                    <RssFeedButton
                                        config={props.config}
                                        className={className}
                                        type={type}
                                        text={text}
                                        showIcon={showIcon}
                                    />
                                );
                            }
                            case "searchtextbox": {
                                return (
                                    <SearchTextbox value={props.searchText} onSearch={props.onSearch} />
                                );
                            }
                            case "settingsbutton": {
                                const className = getAttributeValue(domNode, "class", true);
                                const type = getAttributeValue(domNode, "type", true);
                                const text = getAttributeValue(domNode, "text", true);
                                const title = getAttributeValue(domNode, "title", true);
                                const ariaLabel = getAttributeValue(domNode, "aria-label", true);
                                const showIcon = getAttributeValue(domNode, "show-icon", true) !== "false";
                                return (
                                    <SettingsButton
                                        isAdmin={props.isAdmin}
                                        onClick={props.onSettings}
                                        type={type}
                                        text={text}
                                        title={title}
                                        ariaLabel={ariaLabel}
                                        showIcon={showIcon}
                                    />
                                );
                            }
                            case "showarchivedbutton": {
                                const className = getAttributeValue(domNode, "class", true);
                                const type = getAttributeValue(domNode, "type", true);
                                const archivedText = getAttributeValue(domNode, "archived-text", true);
                                const unarchivedText = getAttributeValue(domNode, "unarchived-text", true);
                                const showIcon = getAttributeValue(domNode, "show-icon", true) !== "false";
                                const archivedTitle = getAttributeValue(domNode, "archived-title", true);
                                const unarchivedTitle = getAttributeValue(domNode, "unarchived-title", true);
                                const archivedAriaLabel = getAttributeValue(domNode, "archived-aria-label", true);
                                const unarchivedAriaLabel = getAttributeValue(domNode, "unarchived-aria-label", true);
                                return (
                                    <ShowArchivedButton
                                        isAdmin={props.isAdmin}
                                        includeDeleted={props.isViewingArchivedItems}
                                        onClick={props.onShowArchived}
                                        className={className}
                                        type={type}
                                        archivedText={archivedText}
                                        unarchivedText={unarchivedText}
                                        archivedTitle={archivedTitle}
                                        unarchivedTitle={unarchivedTitle}
                                        archivedAriaLabel={archivedAriaLabel}
                                        unarchivedAriaLabel={unarchivedAriaLabel}
                                    />
                                );
                            }
                            default:
                                return domNode;
                        }
                    } catch (ex) {
                        console.error(ex);
                        return null;
                    }
                },
            })}
        </>
    );
};

export default Template;
